import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * 3-image fleet gallery: large hero + two thumbnails.
 * Click any tile to open a lightbox with keyboard + arrow navigation.
 */
export const FleetGallery = ({ images = [], vehicleName = "" }) => {
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i === null ? null : (i - 1 + images.length) % images.length)), [images.length]);
  const next = useCallback(() => setActive((i) => (i === null ? null : (i + 1) % images.length)), [images.length]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active, close, prev, next]);

  if (!images.length) return null;

  const [hero, ...thumbs] = images;

  return (
    <div data-testid="fleet-gallery">
      <div className="grid grid-cols-4 grid-rows-2 gap-3 h-[280px] md:h-[420px]">
        {/* hero — spans 3 cols, 2 rows */}
        <button
          type="button"
          onClick={() => setActive(0)}
          data-testid="gallery-hero-btn"
          className="col-span-4 row-span-2 md:col-span-3 relative rounded-2xl overflow-hidden group cursor-zoom-in"
        >
          <img src={hero.src} alt={hero.alt} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
          {hero.caption && (
            <span className="absolute bottom-3 left-3 text-white text-xs font-bold uppercase tracking-[0.2em] bg-ink/70 backdrop-blur px-3 py-1.5 rounded-full">
              {hero.caption}
            </span>
          )}
        </button>
        {/* thumbnails */}
        {thumbs.slice(0, 2).map((t, i) => (
          <button
            key={t.src}
            type="button"
            onClick={() => setActive(i + 1)}
            data-testid={`gallery-thumb-${i}`}
            className="hidden md:block col-span-1 row-span-1 relative rounded-2xl overflow-hidden group cursor-zoom-in"
          >
            <img src={t.src} alt={t.alt} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
            {t.caption && (
              <span className="absolute bottom-2 left-2 text-white text-[10px] font-bold uppercase tracking-[0.18em] bg-ink/70 backdrop-blur px-2 py-1 rounded-full">
                {t.caption}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* mobile-only thumb row */}
      <div className="md:hidden mt-3 grid grid-cols-2 gap-3">
        {thumbs.slice(0, 2).map((t, i) => (
          <button
            key={t.src}
            type="button"
            onClick={() => setActive(i + 1)}
            className="relative rounded-2xl overflow-hidden aspect-[4/3]"
          >
            <img src={t.src} alt={t.alt} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
            {t.caption && (
              <span className="absolute bottom-2 left-2 text-white text-[10px] font-bold uppercase tracking-[0.18em] bg-ink/70 backdrop-blur px-2 py-1 rounded-full">
                {t.caption}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-ink/95 backdrop-blur-md grid place-items-center p-4"
            onClick={close}
          >
            <button
              type="button"
              data-testid="gallery-close"
              onClick={close}
              className="absolute top-4 right-4 md:top-6 md:right-6 grid place-items-center h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              aria-label="Close gallery"
            >
              <X className="h-5 w-5" />
            </button>

            <button
              type="button"
              data-testid="gallery-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 grid place-items-center h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              data-testid="gallery-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 grid place-items-center h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              aria-label="Next image"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-6xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[active].src}
                alt={images[active].alt}
                className="w-full max-h-[80vh] object-contain rounded-2xl"
              />
              <div className="mt-4 flex items-center justify-between text-white/70 text-sm">
                <span>{images[active].caption || `${vehicleName} — image ${active + 1}`}</span>
                <span className="text-white/40 text-xs uppercase tracking-widest">{active + 1} / {images.length}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
